<template>
  <div class="w-90 relative">
    <Transition name="auth-fade" mode="out-in">
      <div
        v-if="viewMode === 'login' || viewMode === 'register'"
        key="auth-main"
        class="flex flex-col w-full gap-4"
      >
        <div ref="authCardRef" class="rounded-4xl p-4 bg-white">
          <Transition name="auth-fade" mode="out-in">
            <h2 :key="viewMode" class="text-4xl-semibold text-center mb-8 select-none">
              {{ viewMode === 'register' ? $t('home.user.register') : $t('home.user.login') }}
            </h2>
          </Transition>

          <div class="w-full">
            <div class="flex w-full justify-start gap-4 bg-transparent items-start">
              <button
                v-for="tab in tabs"
                :key="tab.value"
                class="rounded-none w-fit pb-0 cursor-pointer transition-colors leading-6 box-border underline-offset-3 select-none"
                :class="[
                  currentTab.value === tab.value
                    ? 'text-text-active underline '
                    : 'hover:text-text-active text-text-body-default',
                ]"
                @click="currentTab = tab"
              >
                {{ tab.label }}
              </button>
            </div>
            <div class="mt-6">
              <Transition
                name="auth-fade"
                mode="out-in"
                @before-leave="lockAuthCardHeight"
                @enter="animateAuthCardHeight"
                @after-enter="releaseAuthCardHeight"
              >
                <component :is="currentTab.component" :key="viewMode + '-' + currentTab.value" />
              </Transition>
            </div>
          </div>
        </div>

        <div class="flex items-center bg-white rounded-4xl w-full h-18 overflow-hidden">
          <Transition name="auth-fade" mode="out-in">
            <div :key="currentTab.value + viewMode" class="w-full">
              <component
                :is="currentTab.footer"
                @click-forgot="switchMode('forgot')"
                @click-register="switchMode('register')"
                @back-login="switchMode('login')"
              />
            </div>
          </Transition>
        </div>
      </div>

      <div v-else-if="viewMode === 'forgot'" key="auth-forgot" class="flex flex-col w-full gap-4">
        <div class="rounded-4xl p-4 bg-white">
          <ForgotPasswordForm @cancel="switchMode('login')" @success="switchMode('login')" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, shallowRef } from 'vue'
  import type { Component } from 'vue'
  import { useLocaleStore } from '@/stores'
  import AccountForm from './login/AccountForm.vue'
  import AccountFooter from './login/AccountFooter.vue'
  import ScanForm from './login/ScanForm.vue'
  import ScanFooter from './login/ScanFooter.vue'
  import ForgotPasswordForm from './forgot-password/ForgotPasswordForm.vue'
  import RegisterForm from './register/RegisterForm.vue'
  import RegisterFooter from './register/RegisterFooter.vue'

  type ViewMode = 'login' | 'forgot' | 'register'

  interface TabItem {
    value: string
    label: string
    component: Component
    footer: Component
  }

  const viewMode = ref<ViewMode>('login')
  const authCardRef = ref<HTMLElement>()
  let leavingContentHeight = 0
  const { t } = useLocaleStore()

  const loginTabs = computed<TabItem[]>(() => [
    {
      value: 'account',
      label: t('home.user.loginTab.account'),
      component: AccountForm,
      footer: AccountFooter,
    },
    {
      value: 'scan',
      label: t('home.user.loginTab.scan'),
      component: ScanForm,
      footer: ScanFooter,
    },
  ])

  const registerTabs = computed<TabItem[]>(() => [
    {
      value: 'account',
      label: t('home.user.registerTab.account'),
      component: RegisterForm,
      footer: RegisterFooter,
    },
    {
      value: 'scan',
      label: t('home.user.registerTab.scan'),
      component: ScanForm,
      footer: RegisterFooter,
    },
  ])

  const tabs = computed(() =>
    viewMode.value === 'register' ? registerTabs.value : loginTabs.value,
  )
  const currentTab = shallowRef<TabItem>(loginTabs.value[0])

  const switchMode = (mode: ViewMode) => {
    viewMode.value = mode
    currentTab.value = mode === 'register' ? registerTabs.value[0] : loginTabs.value[0]
  }

  const lockAuthCardHeight = (element: Element) => {
    const card = authCardRef.value
    if (!card) return

    leavingContentHeight = (element as HTMLElement).offsetHeight
    card.style.height = `${card.offsetHeight}px`
    card.style.overflow = 'hidden'
  }

  const animateAuthCardHeight = (element: Element) => {
    const card = authCardRef.value
    if (!card) return

    const startHeight = card.offsetHeight
    const enteringContentHeight = (element as HTMLElement).offsetHeight
    const targetHeight = startHeight - leavingContentHeight + enteringContentHeight

    card.style.transition = 'height 0.25s ease'
    requestAnimationFrame(() => {
      card.style.height = `${targetHeight}px`
    })
  }

  const releaseAuthCardHeight = () => {
    const card = authCardRef.value
    if (!card) return

    card.style.removeProperty('height')
    card.style.removeProperty('overflow')
    card.style.removeProperty('transition')
  }
</script>

<style scoped>
  .auth-fade-enter-active,
  .auth-fade-leave-active {
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
  }

  .auth-fade-enter-from {
    opacity: 0;
    transform: translateY(4px);
  }

  .auth-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
  }
</style>
